import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/app/api";

// Global cache for blob URLs (persists across component lifecycles)
const blobUrlCache = new Map<string, string>();

// Track which blobs are currently being used to prevent premature revocation
const blobRefCounts = new Map<string, number>();

/**
 * Increment reference count for a blob URL
 */
export function retainBlobUrl(filename: string) {
  const count = blobRefCounts.get(filename) || 0;
  blobRefCounts.set(filename, count + 1);
}

/**
 * Decrement reference count for a blob URL
 * Note: We don't revoke blob URLs anymore since they should persist
 * for the lifetime of the page to avoid cache returning revoked URLs
 */
export function releaseBlobUrl(filename: string) {
  const count = blobRefCounts.get(filename) || 0;
  if (count <= 1) {
    blobRefCounts.delete(filename);
    // Don't revoke - keep blob URLs alive for cache consistency
  } else {
    blobRefCounts.set(filename, count - 1);
  }
}

/**
 * Get cached blob URL or null if not cached
 */
export function getCachedBlobUrl(filename: string | null): string | null {
  if (!filename) return null;
  return blobUrlCache.get(filename) || null;
}

/**
 * Hook to batch-prefetch all product images for a list of products
 * This should be called at the page level to prefetch all images at once
 */
export function useProductImagesBatch(
  imageUrls: (string | null | undefined)[],
) {
  const queryClient = useQueryClient();

  // Filter out null/undefined and duplicates
  const uniqueUrls = Array.from(
    new Set(imageUrls.filter((url): url is string => Boolean(url))),
  );

  // Batch prefetch all images
  const { data: blobUrls, isLoading } = useQuery({
    queryKey: ["product-images-batch", uniqueUrls.sort().join(",")],
    queryFn: async () => {
      const results = new Map<string, string>();

      // Check cache first and separate uncached URLs
      const uncachedUrls = uniqueUrls.filter((url) => {
        const cached = blobUrlCache.get(url);
        if (cached) {
          results.set(url, cached);
          return false;
        }
        return true;
      });

      // If all are cached, return immediately
      if (uncachedUrls.length === 0) {
        return results;
      }

      // Batch fetch uncached images (max 50 per batch request)
      const batchSize = 50;
      for (let i = 0; i < uncachedUrls.length; i += batchSize) {
        const batch = uncachedUrls.slice(i, i + batchSize);

        try {
          // Single batch request for up to 50 images
          const blobsMap = await api.products.getMediaBatch(batch);

          // Create blob URLs and cache them
          for (const [url, blob] of Object.entries(blobsMap)) {
            const blobUrl = URL.createObjectURL(blob);
            blobUrlCache.set(url, blobUrl);
            results.set(url, blobUrl);
          }
        } catch (error) {
          console.error(`Failed to load batch of images:`, error);
          // Fallback: Try individual loads for this batch
          await Promise.allSettled(
            batch.map(async (url) => {
              try {
                const blob = await api.products.getMedia(url);
                const blobUrl = URL.createObjectURL(blob);
                blobUrlCache.set(url, blobUrl);
                results.set(url, blobUrl);
              } catch (err) {
                console.error(`Failed to load image ${url}:`, err);
              }
            }),
          );
        }
      }

      return results;
    },
    staleTime: Infinity, // Images don't change, cache forever
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    retry: false, // Don't retry failed image loads (e.g. missing MinIO files)
    enabled: uniqueUrls.length > 0,
  });

  // Prefetch individual images into TanStack Query cache for component-level access
  useEffect(() => {
    if (blobUrls) {
      // Convert to array of entries if it's a Map, otherwise treat as object
      const entries =
        blobUrls instanceof Map
          ? Array.from(blobUrls.entries())
          : Object.entries(blobUrls);

      entries.forEach(([originalUrl, blobUrl]) => {
        queryClient.setQueryData(["product-image", originalUrl], {
          url: blobUrl,
          loading: false,
          error: null,
        });
      });
    }
  }, [blobUrls, queryClient]);

  return { isLoading, count: uniqueUrls.length };
}

/**
 * Hook to get a single product image URL
 * Works with batch prefetching or can load individually as fallback
 */
export function useProductImage(filename: string | null) {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["product-image", filename],
    queryFn: async () => {
      if (!filename) {
        return { url: null, loading: false, error: null };
      }

      // Check cache first
      const cached = blobUrlCache.get(filename);
      if (cached) {
        return { url: cached, loading: false, error: null };
      }

      // Fallback: Load individually if not prefetched
      try {
        const blob = await api.products.getMedia(filename);
        const url = URL.createObjectURL(blob);
        blobUrlCache.set(filename, url);
        return { url, loading: false, error: null };
      } catch (err) {
        console.error("Failed to load product image:", err);
        return {
          url: null,
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load image",
        };
      }
    },
    staleTime: Infinity, // Images don't change
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    retry: false, // Don't retry failed image loads
    enabled: Boolean(filename),
  });

  // If we have cached query data but the blob URL is no longer in our cache,
  // invalidate the query to refetch
  useEffect(() => {
    if (data?.url && filename && !blobUrlCache.has(filename)) {
      queryClient.invalidateQueries({ queryKey: ["product-image", filename] });
    }
  }, [data?.url, filename, queryClient]);

  // Track blob URL usage
  useEffect(() => {
    if (filename && data?.url) {
      retainBlobUrl(filename);
      return () => {
        releaseBlobUrl(filename);
      };
    }
  }, [filename, data?.url]);

  return {
    url: data?.url || null,
    loading: isLoading,
    error: data?.error || (error ? "Failed to load image" : null),
  };
}
